import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ICoachingForm, CoachingFormAnswerType } from '../services/api/coachingForm/types';
import { CoachingType, IProtocol } from '../services/api/protocol/protocolModel';
import {
    calculateSectionPoints,
    createEmptyProtocolState,
    createProtocolState,
} from '../components/coaching/form/coachingForm.utils';
import {
    ProtocolFormPointMap,
    ProtocolFormState,
} from '../components/coaching/form/coachingForm.types';

export const useCoachInteractionStore = defineStore('coach-interaction', () => {
    const protocol = ref<ProtocolFormState>({
        protocolId: '',
        coachingType: CoachingType.REMOTE,
        channelId: undefined,
        sections: [],
    });

    const agent = ref<{ value: string; name: string } | null>(null);
    const manualInteractionReference = ref<string | undefined>(undefined);

    const isManual = ref(false);

    const sectionPoints = computed<ProtocolFormPointMap>(() => {
        return calculateSectionPoints(protocol.value);
    });

    const isValid = computed(() => {
        const reasonCodes = protocol.value.reasonCodes;
        if (reasonCodes) {
            const threshold = Number(reasonCodes.threshold);
            const totalPoints = sectionPoints.value.total?.points ?? 0;
            const shouldShowReasonCodes = totalPoints <= threshold;

            if (shouldShowReasonCodes) {
                if (!reasonCodes.selectedParentId) {
                    return false;
                }

                const selectedParent = reasonCodes.codes.find(
                    (parent) => parent.id === reasonCodes.selectedParentId
                );

                const childOptions = selectedParent?.childCodes ?? [];

                if (childOptions.length > 0 && !reasonCodes.selectedChildId) {
                    return false;
                }
            }
        }
        if (isManual.value) {
            return !!agent.value && !!protocol.value.channelId;
        }
        for (const section of protocol.value.sections) {
            for (const question of section.questions) {
                if (
                    (question.answerType === CoachingFormAnswerType.Interval ||
                        question.answerType === CoachingFormAnswerType.Affirmative ||
                        question.answerType === CoachingFormAnswerType.Custom) &&
                    !question.selectedOptionId
                ) {
                    return false;
                }
            }
        }
        return true;
    });

    const setProtocol = (formData: ICoachingForm, protocolData: IProtocol) => {
        protocol.value = createProtocolState(formData, protocolData);
    };

    const initProtocol = (formData: ICoachingForm, manual?: boolean) => {
        protocol.value = createEmptyProtocolState(formData);
        isManual.value = !!manual;
    };

    return {
        protocol,
        agent,
        manualInteractionReference,
        isValid,
        isManual,
        setProtocol,
        initProtocol,
        sectionPoints,
    };
});
